/**
 * src/components/agent/ScopeFilterPill.jsx — Sidebar unified scope filter pill.
 *
 * TICKET-233. Generalizes ProjectFilterPill from project-only to a unified
 * scope filter: All / Unassigned + grouped sections (Projects / Pipeline /
 * Tasks / People). The chat schema already links conversations to
 * project/deal/task/person (migrations 046/047/087) — this exposes all of them
 * in one dropdown so chats about a pipeline deal are as filterable as project
 * chats (CEO 2026-07-06: "chat filter only contemplates projects").
 *
 * Controlled by `value` (null | '__unassigned__' | { type, id }) + `onChange`.
 * `groups`: [{ type, label, items: [{ id, name }] }] — already scoped/sorted by
 * the caller (e.g. Pipeline = OPEN deals only). Type-to-search filters across
 * every group at once (debounced 150ms), mirroring the old project pill.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Filter, ChevronDown, Search } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import {
  filterPillStyle,
  filterPillLabelStyle,
  filterPopoverStyle,
  filterOptionStyle,
  filterOptionActiveStyle,
  filterSearchWrapStyle,
  filterSearchInputStyle,
  filterEmptyStyle,
  filterSectionHeaderStyle,
} from './ChatSidebarV2.styles';

const DEBOUNCE_MS = 150;
const RESULT_CAP = 50; // per group

const isSameScope = (value, type, id) =>
  !!value && value !== '__unassigned__' && value.type === type && value.id === id;

export default function ScopeFilterPill({ value, groups = [], onChange }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // Reopening always starts with a clean query.
  useEffect(() => {
    if (!open) { setQuery(''); setDebounced(''); }
  }, [open]);

  const labelAll = t('agentChat.allChats');
  const labelUnassigned = t('agentChat.unassigned');

  // Resolve the pill's current label from the active scope descriptor.
  // (!value covers both null and an undefined descriptor.)
  const currentLabel = useMemo(() => {
    if (!value) return labelAll;
    if (value === '__unassigned__') return labelUnassigned;
    for (const g of groups) {
      if (g.type !== value.type) continue;
      const hit = (g.items || []).find(i => i.id === value.id);
      if (hit) return hit.name || t('agentChat.unnamed');
    }
    return t('agentChat.scope');
  }, [value, groups, labelAll, labelUnassigned, t]);

  const needle = (debounced || '').trim().toLowerCase();

  // Filter each group's items by the needle, cap per group, drop empty groups.
  const visibleGroups = useMemo(() => groups
    .map(g => ({
      ...g,
      items: (needle
        ? (g.items || []).filter(i => (i.name || '').toLowerCase().includes(needle))
        : (g.items || [])
      ).slice(0, RESULT_CAP),
    }))
    .filter(g => g.items.length > 0), [groups, needle]);

  const showAll = !needle || labelAll.toLowerCase().includes(needle);
  const showUnassigned = !needle || labelUnassigned.toLowerCase().includes(needle);
  const nothingMatches = !showAll && !showUnassigned && visibleGroups.length === 0;

  const choose = v => { onChange(v); setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        style={filterPillStyle}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="chat-sidebar-filter"
      >
        <Filter size={12} color="var(--tm)" />
        <span style={filterPillLabelStyle}>{currentLabel}</span>
        <ChevronDown size={12} color="var(--tm)" />
      </button>

      {open && (
        <div role="listbox" style={filterPopoverStyle}>
          <div style={filterSearchWrapStyle}>
            <Search size={14} color="var(--tm)" style={{ flexShrink: 0 }} />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('agentChat.filterScopes')}
              style={filterSearchInputStyle}
              data-testid="chat-sidebar-filter-search"
              autoFocus
            />
          </div>

          {showAll && (
            <button
              type="button"
              role="option"
              aria-selected={value === null}
              style={value === null ? filterOptionActiveStyle : filterOptionStyle}
              onClick={() => choose(null)}
              data-testid="chat-sidebar-filter-all"
            >
              {labelAll}
            </button>
          )}
          {showUnassigned && (
            <button
              type="button"
              role="option"
              aria-selected={value === '__unassigned__'}
              style={value === '__unassigned__' ? filterOptionActiveStyle : filterOptionStyle}
              onClick={() => choose('__unassigned__')}
              data-testid="chat-sidebar-filter-unassigned"
            >
              {labelUnassigned}
            </button>
          )}

          {visibleGroups.map(g => (
            <div key={g.type}>
              <div style={filterSectionHeaderStyle} data-testid={`chat-sidebar-filter-group-${g.type}`}>
                {g.label}
              </div>
              {g.items.map(item => {
                const active = isSameScope(value, g.type, item.id);
                return (
                  <button
                    key={`${g.type}:${item.id}`}
                    type="button"
                    role="option"
                    aria-selected={active}
                    style={active ? filterOptionActiveStyle : filterOptionStyle}
                    onClick={() => choose({ type: g.type, id: item.id })}
                    data-testid={`chat-sidebar-filter-${g.type}-${item.id}`}
                  >
                    {item.name || t('agentChat.unnamed')}
                  </button>
                );
              })}
            </div>
          ))}

          {nothingMatches && (
            <div style={filterEmptyStyle} data-testid="chat-sidebar-filter-empty">
              {t('agentChat.noScopesMatch')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
